import { useState } from 'react'
import { CheckCircleIcon, ClockIcon, XCircleIcon } from '../Icons'
import { Product } from '../../data/products'

interface LabOrder {
  id: string
  items: Product[]
  total: number
  status: 'pending' | 'confirmed' | 'cancelled' | 'paid'
  createdAt: string
  customerName: string
}

interface LabAdminPanelV3Props {
  onLog: (message: string) => void
  orders: LabOrder[]
  onConfirmOrder: (orderId: string) => void
  onCancelOrder: (orderId: string) => void
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  yellow: '#fbbf24',
  red: '#ef4444',
  blue: '#3b82f6',
}

export default function LabAdminPanelV3({ onLog, orders, onConfirmOrder, onCancelOrder }: LabAdminPanelV3Props) {
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled' | 'paid'>('all')

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed': return { Icon: CheckCircleIcon, color: colors.green }
      case 'paid': return { Icon: CheckCircleIcon, color: colors.blue }
      case 'pending': return { Icon: ClockIcon, color: colors.yellow }
      default: return { Icon: XCircleIcon, color: colors.red }
    }
  }

  const filteredOrders = orders.filter(o => {
    if (filter === 'all') return true
    return o.status === filter
  })

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      color: colors.text,
      padding: '16px',
      paddingBottom: '140px',
    }}>
      <div style={{
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: `2px solid ${colors.accent}`,
      }}>
        <h1 style={{
          margin: 0,
          fontSize: '22px',
          fontWeight: 700,
          color: colors.text,
        }}>
          📊 Админ v3
        </h1>
        <p style={{
          margin: '4px 0 0 0',
          fontSize: '13px',
          color: colors.textMuted,
        }}>
          Табличный формат
        </p>
      </div>

      {/* Фильтры */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        overflowX: 'auto',
      }}>
        {[
          { label: 'Все', value: 'all' as const },
          { label: 'Ожидают', value: 'pending' as const },
          { label: 'Подтверждено', value: 'confirmed' as const },
          { label: 'Оплачено', value: 'paid' as const },
          { label: 'Отменено', value: 'cancelled' as const },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => {
              setFilter(f.value)
              onLog(`v3: Фильтр ${f.label}`)
            }}
            style={{
              background: filter === f.value ? colors.accent : colors.cardBg,
              border: filter === f.value ? 'none' : `1px solid rgba(255, 255, 255, 0.12)`,
              borderRadius: '8px',
              padding: '8px 16px',
              color: colors.text,
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Таблица заказов */}
      <div style={{
        background: colors.cardBg,
        borderRadius: '12px',
        border: `1px solid rgba(255, 255, 255, 0.08)`,
        overflow: 'hidden',
      }}>
        {/* Заголовок таблицы */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '60px 1fr 80px 90px',
          gap: '8px',
          padding: '12px 16px',
          background: colors.nestedBg,
          fontSize: '12px',
          fontWeight: 600,
          color: colors.textMuted,
          borderBottom: `1px solid rgba(255, 255, 255, 0.08)`,
        }}>
          <div>ID</div>
          <div>Клиент</div>
          <div>Сумма</div>
          <div>Действия</div>
        </div>

        {/* Строки */}
        {filteredOrders.map((order) => {
          const { Icon, color } = getStatusIcon(order.status)
          return (
            <div
              key={order.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 80px 90px',
                gap: '8px',
                padding: '16px',
                borderBottom: `1px solid rgba(255, 255, 255, 0.04)`,
                background: 'transparent',
                opacity: order.status === 'cancelled' ? 0.5 : 1,
              }}
            >
              <div style={{
                fontSize: '13px',
                color: colors.textMuted,
                fontFamily: 'monospace',
              }}>
                {order.id}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
              }}>
                <Icon size={16} color={color} />
                {order.customerName}
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: colors.accent,
              }}>
                ₽{order.total.toLocaleString()}
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {order.status === 'pending' && (
                  <>
                    <button
                      onClick={() => onConfirmOrder(order.id)}
                      style={{
                        background: colors.green,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px',
                        color: '#fff',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => onCancelOrder(order.id)}
                      style={{
                        background: colors.red,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px',
                        color: '#fff',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      ✕
                    </button>
                  </>
                )}
                {order.status === 'confirmed' && (
                  <div style={{
                    fontSize: '11px',
                    color: colors.green,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    ✓ OK
                  </div>
                )}
                {order.status === 'paid' && (
                  <div style={{
                    fontSize: '11px',
                    color: colors.blue,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    💳 Paid
                  </div>
                )}
                {order.status === 'cancelled' && (
                  <div style={{
                    fontSize: '11px',
                    color: colors.red,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    ✕ Нет
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Итого */}
      <div style={{
        marginTop: '16px',
        background: `linear-gradient(135deg, ${colors.accent}15, transparent)`,
        border: `1px solid ${colors.accent}30`,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: '13px', color: colors.textMuted }}>
            Отображено
          </div>
          <div style={{ fontSize: '20px', fontWeight: 700, marginTop: '4px' }}>
            {filteredOrders.length} / {orders.length}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '13px', color: colors.textMuted }}>
            Оплачено
          </div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: colors.green, marginTop: '4px' }}>
            {orders.filter(o => o.status === 'paid').length}
          </div>
        </div>
      </div>
    </div>
  )
}
